import React, { useState, useEffect } from "react";
import { Button, Checkbox, MultiSelect, Select, Text } from "@mantine/core";
import { showNotification } from "@mantine/notifications";
import { BeatLoader } from "react-spinners";
import {
  loadingStates,
  validateEmail,
  validateMobile,
} from "../../utilities/utilities";
import colors from "../../utilities/design";
import { getUserRolesUsingRoleId } from "../../utilities/apis/serviceProvider";
import {
  getAgreementDetails,
  getuserIdbyMail,
  putflowJson,
} from "../../utilities/apis/agreements";
import { apiWrapWithErrorWithData } from "../../utilities/apiHelpers";
import { useSelector } from "react-redux";
import { selectUserData } from "../../redux/selectors";

const mapping = {
  Initiator: "3",
  Editor: "4",
  Reviewer: "5",
  Approver: "6",
  SigningAuthority: "7",
};

const AgreementForm5 = ({
  flowData,
  setFlowData,
  form,
  setUiConfigs,
  uiConfigs,
  save,
  id,
}) => {
  const currentUser = useSelector(selectUserData);
  const [isLoading, setIsLoading] = useState(true);
  const [userRolesIdData, setUserRolesIdData] = useState({
    userRoles: {
      Initiator: [],
      Editor: [],
      Reviewer: [],
      Approver: [],
      SigningAuthority: [],
    },
  });
  const [checked, setChecked] = useState(
    Boolean(flowData?.sendfordirectsign),
  );
  const [dataRole, setDataRole] = useState(flowData);

  const handleDirectSignChange = (isChecked) => {
    setChecked(isChecked);
    setDataRole((prev) => {
      const next = {
        ...prev,
        sendfordirectsign: isChecked,
      };
      if (isChecked) {
        next["4"] = [];
        next["5"] = [];
        next["6"] = [];
        const existingInitiators = Array.isArray(prev["3"]) ? prev["3"] : [];
        next["3"] =
          existingInitiators.length > 0
            ? existingInitiators.map((user) => ({
                ...user,
                userId: Number(user.userId),
                isComplete: false,
              }))
            : [{ userId: Number(currentUser.id), isComplete: false }];
      }
      return next;
    });
  };

  useEffect(() => {
    const fetchData = async () => {
      const agreementId = id;
      const { data } = await getAgreementDetails({ agreementId });
      const fetchedDataRole = data.agreement.flowJson;

      if (fetchedDataRole) {
        setDataRole(fetchedDataRole);
        setChecked(Boolean(fetchedDataRole.sendfordirectsign));
      } else {
        const loginAsId = Number(currentUser.loginAs.id);
        if (loginAsId === 4 || loginAsId === 3) {
          setDataRole((prevDataRole) => ({
            ...prevDataRole,
            [loginAsId + ""]: [
              {
                userId: currentUser.id,
                isComplete: false,
              },
            ],
          }));
        }
      }
    };

    fetchData();
  }, [id, currentUser.loginAs.id]);

  useEffect(() => {
    const fetchUserRoles = async () => {
      try {
        const roles = {
          Initiator: (await getUserRolesUsingRoleId(3)).data.result,
          Editor: (await getUserRolesUsingRoleId(4)).data.result,
          Reviewer: (await getUserRolesUsingRoleId(5)).data.result,
          Approver: (await getUserRolesUsingRoleId(6)).data.result,
          SigningAuthority: (await getUserRolesUsingRoleId(7)).data.result,
        };

        setUserRolesIdData((prevData) => ({
          ...prevData,
          userRoles: roles,
        }));
      } catch (error) {
        console.error("Error fetching user roles:", error);
      } finally {
        setIsLoading(false);
      }
    };

    fetchUserRoles();
  }, []);

  const savePrev = async () => {
    if (true) {
      setUiConfigs({
        ...uiConfigs,
        currentStep: 2,
      });
    }
  };

  const updateClick = async () => {
    if (
      (checked &&
        (!dataRole["7"] ||
          dataRole["7"].length === 0 ||
          dataRole["7"].length > 2)) ||
      (!checked &&
        (!dataRole["4"] ||
          dataRole["4"].length === 0 ||
          !dataRole["7"] ||
          dataRole["7"].length === 0 ||
          dataRole["7"].length > 2))
    ) {
      showNotification({
        color: "red",
        title: "Agreement Form",
        message:
          "Please fill in all required fields and ensure maximum 2 users for Signing Authority.",
      });
      return;
    }

    try {
      await apiWrapWithErrorWithData(putflowJson(id, dataRole));
      showNotification({
        color: "green",
        title: "Update User Roles",
        message: "User Roles Updated.",
      });
      setUiConfigs({
        ...uiConfigs,
        currentStep: 4,
      });
    } catch (error) {
      console.error("An error occurred:", error);
    }
  };

  const handleSelectChange = (role, selectedUser) => {
    const mappingRole = mapping[role];
    const selectedData = selectedUser
      ? [
          {
            userId: Number(selectedUser),
            isComplete: false,
          },
        ]
      : [];

    setDataRole((prevDataRole) => ({
      ...prevDataRole,
      [mappingRole]: selectedData,
    }));
  };

  const handleMultiSelectChange = (role, selectedUsers) => {
    const mappingRole = mapping[role];

    const selectedData = selectedUsers.map((userId) => {
      const numericUserId = Number(userId);
      const existingUser = dataRole[mappingRole]?.find(
        (user) => Number(user.userId) === numericUserId,
      );
      return {
        userId: numericUserId,
        isComplete: existingUser ? existingUser.isComplete : false,
      };
    });

    setDataRole((prevDataRole) => ({
      ...prevDataRole,
      [mappingRole]: selectedData,
    }));
  };

  return (
    <div className="w-full flex flex-col my-4">
      {Number(currentUser.loginAs?.id) === 3 && (
        <Checkbox
          checked={checked}
          label="send for signature directly"
          onChange={(event) =>
            handleDirectSignChange(event.currentTarget.checked)
          }
        />
      )}
      <div className="grid grid-cols-2 gap-x-10 ">
        {isLoading ? (
          <div className="col-span-3 flex justify-center">
            <BeatLoader color={colors.primary} size={10} />
          </div>
        ) : (
          <>
            <Select
              key="Initiator"
              label="initiator"
              className="mt-8"
              placeholder="initiator"
              clearable
              searchable
              value={
                dataRole["3"]?.[0]?.userId != null
                  ? String(dataRole["3"][0].userId)
                  : null
              }
              onChange={(selectedUser) =>
                handleSelectChange("Initiator", selectedUser)
              }
              data={userRolesIdData.userRoles.Initiator.map((user) => ({
                value: String(user.id),
                label: user.name,
              }))}
            />
            {!checked && (
              <Select
                key="Editor"
                label="editor"
                className="mt-8"
                placeholder="editor"
                clearable
                searchable
                value={
                  dataRole["4"]?.[0]?.userId != null
                    ? String(dataRole["4"][0].userId)
                    : null
                }
                onChange={(selectedUser) =>
                  handleSelectChange("Editor", selectedUser)
                }
                data={userRolesIdData.userRoles.Editor.map((user) => ({
                  value: String(user.id),
                  label: user.name,
                }))}
                required={!checked}
              />
            )}
            {!checked && (
              <MultiSelect
                key="Reviewer"
                label="reviewer/s"
                className="mt-8"
                placeholder="reviewer/s"
                searchable
                clearable
                value={(dataRole["5"] || []).map(({ userId }) =>
                  String(userId),
                )}
                onChange={(selectedUsers) =>
                  handleMultiSelectChange("Reviewer", selectedUsers)
                }
                data={userRolesIdData.userRoles.Reviewer.map((user) => ({
                  value: String(user.id),
                  label: user.name,
                }))}
              />
            )}
            {!checked && (
              <MultiSelect
                key="Approver"
                label="approver/s"
                className="mt-8"
                placeholder="approver/s"
                searchable
                clearable
                value={(dataRole["6"] || []).map(({ userId }) =>
                  String(userId),
                )}
                onChange={(selectedUsers) =>
                  handleMultiSelectChange("Approver", selectedUsers)
                }
                data={userRolesIdData.userRoles.Approver.map((user) => ({
                  value: String(user.id),
                  label: user.name,
                }))}
              />
            )}
            {/* <MultiSelect
              key="SigningAuthority"
              label="signing authority"
              className="mt-8"
              placeholder="signing authority"
              value={dataRole["7"]?.map(({ userId }) => userId) || []}
              onChange={(selectedUsers) =>
                handleMultiSelectChange("SigningAuthority", selectedUsers)
              }
              data={userRolesIdData.userRoles.SigningAuthority.map((user) => ({
                value: user.id,
                label: user.name,
              }))}
              maxSelectedValues={2}
              required
            /> */}
            {/*<div></div>
            {uiConfigs.loading !== loadingStates.LOADING && (
              <div className="flex justify-end">
                <Button
                  className="mt-14 align-right "
                  onClick={updateClick}
                  color="green"
                >
                  update
                </Button>
              </div>
            )}*/}
          </>
        )}
      </div>
      <div className="flex items-center justify-between mt-20">
        <Button
          className="max-w-xs "
          onClick={savePrev}
          style={{
            backgroundColor: "#46BDE1",
          }}
          disabled={uiConfigs.loading === loadingStates.LOADING}
        >
          Previous
        </Button>
        <Button
          className="max-w-xs"
          style={{
            backgroundColor: "#46BDE1",
          }}
          onClick={updateClick}
          disabled={uiConfigs.loading === loadingStates.LOADING}
        >
          {uiConfigs.loading === loadingStates.LOADING ? (
            <BeatLoader color={"#fff"} size={10} />
          ) : (
            "Save & Next"
          )}
        </Button>
      </div>
    </div>
  );
};

export default AgreementForm5;
